import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { User } from '../types/user';
import { clearSchoolLogoCache } from './useSchoolLogo';
import { applyIdentity, IDENTITY_CHANGED_EVENT, IdentityChangedDetail } from '../lib/identity';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        console.log('[useAuth] Checking session...');
        
        // Get email and slug — check URL params first (ACV sends them on iframe load
        // before App.tsx useEffect has a chance to persist them to localStorage)
        const urlParams = new URLSearchParams(window.location.search);
        const urlEmail = urlParams.get('email') || urlParams.get('userEmail');
        // A channel share link carries the *channel's* tenant slug, not the viewer's.
        const urlSlug = urlParams.get('channelToken') ? null : urlParams.get('slug');

        // Identity passed by ACV in the URL always wins over the browser cache
        // (clears the previous user's cached session if it's a different person).
        if (urlEmail) applyIdentity(urlEmail, urlSlug);

        const storedEmail = localStorage.getItem('userEmail');
        const storedSlug = localStorage.getItem('slug');

        console.log('[useAuth] Stored credentials:', { email: storedEmail, slug: storedSlug });
        
        // Si hay email en localStorage o URL params, crear usuario temporal inmediatamente
        // para no bloquear la UI mientras se verifica la sesión
        if (storedEmail) {
          console.log('[useAuth] Email found in localStorage, creating temporary user');
          setUser({
            email: storedEmail,
            slug: storedSlug || undefined,
          } as User);
          
          // Set user context in API client
          apiClient.setUserContext(storedEmail, storedSlug || undefined);
          
          // Ahora verificar la sesión en background sin bloquear
          setLoading(false);
          
          // Verificar sesión en background
          try {
            const { data, error } = await apiClient.getSession();
            console.log('[useAuth] Session check result:', { data, error: error?.message });
            
            if (!error && data) {
              console.log('[useAuth] Updating user from session data:', { email: data.email, slug: data.slug });
              setUser(data);
              
              // Update localStorage with the latest user data
              if (data.email) {
                localStorage.setItem('userEmail', data.email);
              }
              
              if (data.slug) {
                localStorage.setItem('slug', data.slug);
              }
            } else if (error) {
              console.warn('[useAuth] Session check failed, but user already set from localStorage:', error);
              // No hacer nada - el usuario ya está establecido desde localStorage
            }
          } catch (sessionErr) {
            console.warn('[useAuth] Error checking session in background:', sessionErr);
            // No hacer nada - el usuario ya está establecido desde localStorage
          }
        } else {
          // No hay email: verificar sesión con timeout para no bloquear la UI (p. ej. backend lento o cold start)
          setLoading(true);
          const SESSION_CHECK_TIMEOUT_MS = 5000; // 10 s máximo de espera
          const timeoutPromise = new Promise<{ data: null; error: Error }>((_, reject) => {
            setTimeout(() => reject(new Error('Session check timeout')), SESSION_CHECK_TIMEOUT_MS);
          });
          try {
            const { data, error } = await Promise.race([
              apiClient.getSession(),
              timeoutPromise,
            ]);
            console.log('[useAuth] Session check result:', { data, error: error?.message });

            if (error) {
              console.error('[useAuth] Session check error:', error);
              setError('Failed to check authentication status');
            } else if (data) {
              console.log('[useAuth] Setting user from session data:', { email: data.email, slug: data.slug });
              setUser(data);

              if (data.email) {
                localStorage.setItem('userEmail', data.email);
              }
              if (data.slug) {
                localStorage.setItem('slug', data.slug);
              }
            }
          } catch (timeoutErr) {
            console.warn('[useAuth] Session check timed out or failed, showing app anyway:', timeoutErr);
            setError(timeoutErr instanceof Error ? timeoutErr.message : 'Session check timeout');
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('[useAuth] Error checking session:', err);
        setError('Failed to check authentication status');
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // ACV can (re)send the signed-in user via postMessage after load — switch to it.
  useEffect(() => {
    const onIdentityChanged = async (e: Event) => {
      const { email, slug } = (e as CustomEvent<IdentityChangedDetail>).detail;
      setUser({ email, slug: slug || undefined } as unknown as User);
      const { data } = await apiClient.getSession();
      if (data && data.email?.toLowerCase() === email.toLowerCase()) setUser(data);
    };
    window.addEventListener(IDENTITY_CHANGED_EVENT, onIdentityChanged);
    return () => window.removeEventListener(IDENTITY_CHANGED_EVENT, onIdentityChanged);
  }, []);

  const signInWithEmail = async (email: string) => {
    try {
      console.log('[useAuth] Signing in with email:', email);
      setLoading(true);
      setError(null);
      
      // Check API client state before sign in
      const apiUserBefore = apiClient.getUser();
      console.log('[useAuth] API client user state before sign in:', apiUserBefore);
      
      // Get slug from localStorage for consistency
      const slug = localStorage.getItem('slug');
      console.log('[useAuth] Using slug from localStorage for sign in:', slug);
      
      // Always set the user context in API client before sign in
      apiClient.setUserContext(email, slug || undefined);
      
      const { data, error } = await apiClient.signIn(email, slug || undefined);
      console.log('[useAuth] Sign in result:', { data, error: error?.message });
      
      if (error) {
        console.warn('[useAuth] Sign in failed (backend unavailable?), using local temp user:', error.message);
        // Graceful degradation: create a local temp user so the UI doesn't stay blank
        setUser({ id: email, email, slug: slug || undefined } as User);
        setLoading(false);
        return;
      }
      
      if (data) {
        console.log('[useAuth] Setting user from sign in data:', { email: data.email, slug: data.slug });
        setUser(data);
        
        // Store both email and slug in localStorage
        localStorage.setItem('userEmail', email);
        if (data.slug) {
          localStorage.setItem('slug', data.slug);
        }
        
        // Check API client state after sign in
        const apiUserAfter = apiClient.getUser();
        console.log('[useAuth] API client user state after sign in:', apiUserAfter);
      } else {
        console.log('[useAuth] No user data returned from sign in');
      }
      
      return { success: true, message: 'Signed in successfully!' };
    } catch (err) {
      console.error('[useAuth] Error signing in:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      return { success: false, message: err instanceof Error ? err.message : 'Failed to sign in' };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear all user-related data from localStorage (incl. caché de logo para que se vuelva a cargar en la siguiente sesión)
      localStorage.removeItem('userEmail');
      localStorage.removeItem('slug');
      localStorage.removeItem('user');
      clearSchoolLogoCache();

      const { error } = await apiClient.signOut();
      if (error) throw error;
      
      setUser(null);
      return { success: true };
    } catch (err) {
      console.error('[useAuth] Error signing out:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to sign out' };
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    signInWithEmail,
    signOut,
  };
};